// src/components/coach/charts/power-analysis-graph/PowerAnalysisCalibrationButtons.tsx
import React, { useState, useCallback } from 'react';
import { uiBtn } from '../../../ui/buttonClasses';
import { useErrorTracking } from '../../../../hooks/useErrorTracking';
import { useSessionInfo } from '../../../../hooks/useSessionData';
import { useAuth } from '../../../../contexts/AuthContext';

interface CalibrationButtonProps {
  sessionId: string;
  hasCalibration: boolean;
  onCalibratingChange: (isCalibrating: boolean) => void;
  onCalibrationComplete: () => void | Promise<void>;
}

const CalibrationButton: React.FC<CalibrationButtonProps> = ({
  sessionId,
  hasCalibration,
  onCalibratingChange,
  onCalibrationComplete,
}) => {
  const { session } = useAuth();
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { reportError, trackInteraction } = useErrorTracking('CalibrationButton');

  const handleCalibrate = async () => {
    setIsCalibrating(true);
    onCalibratingChange(true);
    setError(null);

    trackInteraction('calibration_start', {
      sessionId,
      hasExistingCalibration: hasCalibration,
    });

    try {
      const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/calibrate-session'
        : '/api/calibrate-session';

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Calibration failed');
      }

      trackInteraction('calibration_success', {
        sessionId,
        alpha: result.result?.alpha,
        r2: result.result?.r2,
        method: result.result?.method,
        rowsWritten: result.result?.rows_written,
      });

      await onCalibrationComplete();
    } catch (err) {
      console.error('❌ Calibration failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');

      reportError(err instanceof Error ? err : new Error(String(err)), {
        context: 'calibration_button_error',
        sessionId,
        hasExistingCalibration: hasCalibration,
        errorType: 'calibration_api_error',
      });
    } finally {
      setIsCalibrating(false);
      onCalibratingChange(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        data-testid="power-calibrate-button"
        type="button"
        onClick={handleCalibrate}
        disabled={isCalibrating}
        className={`${uiBtn(hasCalibration ? 'outline' : 'secondary', { size: 'sm' })} ${
          isCalibrating ? 'opacity-50 cursor-not-allowed' : ''
        } ${!hasCalibration && !isCalibrating ? 'border-badge-warning-fg/40 text-badge-warning-fg' : ''}`}
        title={hasCalibration ? 'Recalibrate session' : 'Calibrate session'}
      >
        {isCalibrating ? 'Calibrating...' : hasCalibration ? 'Recalibrate' : 'Calibrate'}
      </button>

      {error && (
        <span className="text-xs text-red-600" title={error}>
          Error
        </span>
      )}
    </div>
  );
};

interface RemoveCalibrationButtonProps {
  sessionId: string;
  disabled: boolean;
  onCalibrationRemoved: () => void | Promise<void>;
}

const RemoveCalibrationButton: React.FC<RemoveCalibrationButtonProps> = ({
  sessionId,
  disabled,
  onCalibrationRemoved,
}) => {
  const { session } = useAuth();
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { reportError, trackInteraction } = useErrorTracking('RemoveCalibrationButton');

  const handleRemoveCalibration = async () => {
    setIsRemoving(true);
    setError(null);

    trackInteraction('remove_calibration_start', {
      sessionId,
    });

    try {
      const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocalhost
        ? 'http://localhost:3001/api/remove-calibration'
        : '/api/remove-calibration';

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers.Authorization = `Bearer ${session.access_token}`;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({ sessionId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Remove calibration failed');
      }

      trackInteraction('remove_calibration_success', {
        sessionId,
      });

      await onCalibrationRemoved();
    } catch (err) {
      console.error('❌ Remove calibration failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');

      reportError(err instanceof Error ? err : new Error(String(err)), {
        context: 'remove_calibration_button_error',
        sessionId,
        errorType: 'remove_calibration_api_error',
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={handleRemoveCalibration}
        disabled={isRemoving || disabled}
        className={`${uiBtn('danger', { size: 'sm' })} ${isRemoving || disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        title="Remove calibration and return to raw physics power"
      >
        {isRemoving ? 'Removing...' : 'Remove Calibration'}
      </button>

      {error && (
        <span className="text-xs text-red-600" title={error}>
          Error
        </span>
      )}
    </div>
  );
};

const DEMO_CALIBRATION_READONLY_TITLE =
  'Calibration is read-only for demo sessions. Use one of your uploaded sessions to calibrate.';

const ReadOnlyCalibrationLabel: React.FC<{ title?: string }> = ({
  title = DEMO_CALIBRATION_READONLY_TITLE,
}) => (
  <span className={`${uiBtn('ghost', { size: 'sm' })} cursor-not-allowed opacity-70`} title={title}>
    Calibrated (read-only)
  </span>
);

const PowerAnalysisCalibrationButtons: React.FC<{
  sessionId: string;
  hasCalibration: boolean;
  onCalibratingChange?: (isCalibrating: boolean) => void;
  onCalibrationComplete: () => void | Promise<void>;
  onCalibrationRemoved: () => void | Promise<void>;
}> = ({
  sessionId,
  hasCalibration,
  onCalibratingChange,
  onCalibrationComplete,
  onCalibrationRemoved,
}) => {
  const { data: sessionInfo, isLoading: isLoadingSessionInfo } = useSessionInfo(sessionId);
  const { user } = useAuth();
  const [isCalibrating, setIsCalibrating] = useState(false);

  const handleCalibratingChange = useCallback(
    (busy: boolean) => {
      setIsCalibrating(busy);
      onCalibratingChange?.(busy);
    },
    [onCalibratingChange]
  );

  const isDemoSession = sessionInfo?.isDemo === true;

  if (isLoadingSessionInfo) {
    return <div className="flex items-center gap-2" aria-busy="true" />;
  }

  if (isDemoSession) {
    return (
      <div className="flex items-center gap-2">
        <ReadOnlyCalibrationLabel />
      </div>
    );
  }

  // Team sessions: any coach who can view the session may remove calibration.
  // Legacy (no team_id): uploader only.
  const canRemoveCalibration = sessionInfo?.team_id
    ? Boolean(user?.id)
    : Boolean(sessionInfo?.coach_id && user?.id && sessionInfo.coach_id === user.id);

  return (
    <div className="flex items-center gap-2">
      <CalibrationButton
        sessionId={sessionId}
        hasCalibration={hasCalibration}
        onCalibratingChange={handleCalibratingChange}
        onCalibrationComplete={onCalibrationComplete}
      />
      {hasCalibration && (
        <>
          {canRemoveCalibration ? (
            <RemoveCalibrationButton
              sessionId={sessionId}
              disabled={isCalibrating}
              onCalibrationRemoved={onCalibrationRemoved}
            />
          ) : (
            <ReadOnlyCalibrationLabel title="Only the session uploader can remove calibration on personal sessions" />
          )}
        </>
      )}
    </div>
  );
};

export default PowerAnalysisCalibrationButtons;
